import React, { useContext, useEffect, useState } from 'react';
import { Table, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EnergyConsumption, EnergySubDataRow } from '../../../../../../services/types/Calculator';
import { DropDown, Button } from '../../../../../../shared/components';
import MainContext from '../../../../../App/MainProvider/context';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import { calculateElectricityData } from '../../Utils/moduleCalculates';
import { OptionValue } from '../../../../../../services/types/Misc';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';
import { tooltipPreventOverflowModifiers } from '../../../../../../shared/misc/utils';

interface Props {
  energyConsumption: EnergyConsumption;
  setEnergyConsumption: React.Dispatch<React.SetStateAction<EnergyConsumption>>;
}
const EnergyConsumptionElectricity: React.FC<Props> = ({
  energyConsumption,
  setEnergyConsumption,
}) => {
  const { t } = useTranslation();

  const energyConstructionsnOptions = [
    { id: 0, text: t('Select'), value: '' },
    ...(energyConsumption.constructionTypes
      ? energyConsumption.constructionTypes.map((x) => ({
          id: x.id,
          text: x.name,
          value: x.id.toString(),
        }))
      : []),
  ];

  const { notify } = useContext(MainContext);
  const [selectedValueEnergyConsumptionOption, setSelectedValue] = useState<number | null>(0);
  const [selectedValueFunctionOption, setselectedValueFunction] = useState<number | null>(0);
  const [functionsOptionValues, setFunctionsOptionValues] = useState<OptionValue[]>([]);
  const [energyInputValue, setEnergyInputValue] = useState<string>('');
  const [rows, setRows] = useState<EnergySubDataRow[]>([]);
  const [, setShowSubTypeModal] = useState(false);
  const [showSubModuleButton] = useState(false);
  const [enableFuntionButton, setEnableFuntionButton] = useState(false);
  const [enableInput, setEnableInput] = useState(false);
  const isAddButtonDisabled =
    selectedValueEnergyConsumptionOption === null ||
    selectedValueEnergyConsumptionOption === 0 ||
    selectedValueFunctionOption === null ||
    selectedValueFunctionOption === 0 ||
    Number(energyInputValue) <= 0;

  const allFunciontsOptions = energyConsumption.functionTypes;

  const handleChangeConstruction = (value: number) => {
    setSelectedValue(value);

    const selectedCoverType = energyConsumption.constructionTypes.find((x) => x.id === value);

    if (selectedCoverType) {
      const energyOptions = [
        { id: 0, text: t('Select'), value: '' },
        ...(allFunciontsOptions
          ? allFunciontsOptions
              .filter((x) => x.typeId === value)
              .map((x) => ({
                id: x.id,
                text: x.name,
                value: x.id.toString(),
              }))
          : []),
      ];

      setFunctionsOptionValues(energyOptions);
    }

    setselectedValueFunction(null);
  };

  const handleChangeFunction = (value: number) => {
    setselectedValueFunction(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setEnergyInputValue(value);
    }
  };

  const isDuplicateCoverType = (existingRows: EnergySubDataRow[], coverId: number) => {
    const isDuplicate = existingRows.some((row) => row.functionTypeId === coverId);

    if (isDuplicate) {
      notify(
        'Energy Consumption',
        'A row with this Energy Type already exists.',
        'danger',
        'exclamation-triangle'
      );
    }

    return isDuplicate;
  };

  useEffect(() => {
    if (selectedValueFunctionOption !== null && selectedValueFunctionOption !== 0) {
      setEnableInput(true);
    } else {
      setEnableInput(false);
    }

    if (
      selectedValueEnergyConsumptionOption !== null &&
      selectedValueEnergyConsumptionOption !== 0
    ) {
      setEnableFuntionButton(true);
    } else {
      setEnableFuntionButton(false);
    }
  }, [allFunciontsOptions, selectedValueFunctionOption, selectedValueEnergyConsumptionOption]);

  const handleAddRow = () => {
    if (
      selectedValueEnergyConsumptionOption !== null &&
      selectedValueFunctionOption !== null &&
      Number(energyInputValue) > 0
    ) {
      const selectedConstructionType = energyConsumption.constructionTypes.find(
        (x) => x.id === selectedValueEnergyConsumptionOption
      );

      const selectedFunctionType = energyConsumption.functionTypes.find(
        (x) => x.id === selectedValueFunctionOption
      );

      if (selectedConstructionType && selectedFunctionType) {
        if (isDuplicateCoverType(rows, selectedFunctionType.id)) {
          return;
        }

        const result = calculateElectricityData(
          selectedFunctionType.emissionFactor,
          Number(energyInputValue)
        );
        const newRow: EnergySubDataRow = {
          id: 0,
          constructionTypeId: selectedConstructionType.id,
          constructionType: selectedConstructionType.name,
          functionTypeId: selectedFunctionType.id,
          functionType: selectedFunctionType.name,
          areaValue: Number(energyInputValue),
          quantityValue: result,
        };

        setRows((prevRows) => {
          const updatedRows = [...prevRows, newRow];

          const totalCarbonSum = updatedRows.reduce((sum, row) => sum + row.quantityValue, 0);

          setEnergyConsumption({
            ...energyConsumption,
            eletricityRows: updatedRows,
            eletricityRowsTotal: totalCarbonSum,
          });

          return updatedRows;
        });

        setEnergyInputValue('');
        setSelectedValue(0);
        setselectedValueFunction(0);
      }
    }
  };

  const totalCarbonSum = rows.reduce((sum, row) => sum + row.quantityValue, 0);

  return (
    <>
      <div className="form-row align-items-center calculator-forms table-wrapper-calc">
        <div className="col-sm-2 d-flex align-items-center">
          <label className="col-form-label mr-2" htmlFor="sector">
            {t('Type of Construction')}
          </label>
        </div>
        <div className="col-sm-2 d-flex align-items-center">
          <DropDown
            elements={energyConstructionsnOptions}
            value={selectedValueEnergyConsumptionOption}
            onChange={handleChangeConstruction}
            key="drop-energy"
            className="dropdown-module-calculator-mini"
          />
        </div>
        <div className="col-sm-1 d-flex align-items-center">
          <label className="col-form-label mr-2" htmlFor="sector">
            {t('Function')}
          </label>
        </div>
        <div className="col-sm-2 d-flex align-items-center">
          <DropDown
            elements={functionsOptionValues}
            value={selectedValueFunctionOption}
            onChange={handleChangeFunction}
            key="drop-energy"
            className={`dropdown-module-calculator-mini ${
              !enableFuntionButton ? 'drop-electricity-disabled' : ''
            }`}
          />
        </div>
        <div className="col-sm-1 d-flex align-items-center">
          <label className="col-form-label mr-2" htmlFor="sector">
            {t('Area')} M<sup>2</sup>
          </label>
        </div>

        <div className="col-sm-2 d-flex align-items-center">
          <input
            title="Area"
            type="number"
            id="energyInput"
            value={energyInputValue}
            onChange={handleInputChange}
            min="0"
            max={99999999.99}
            maxLength={10}
            onKeyDown={handleKeyDown}
            className="calculator-input"
            disabled={!enableInput}
          />
          {showSubModuleButton && (
            <>
              <button
                title="Edit"
                type="button"
                className="gi-icon"
                onClick={() => setShowSubTypeModal(true)}
                id="editIcon_electricity"
              >
                <FontAwesomeIcon icon="edit" />
              </button>
              <UncontrolledTooltip
                className="tooltip-toolkit"
                placement="bottom"
                target="editIcon_electricity"
                modifiers={tooltipPreventOverflowModifiers}
              >
                {t('Custom watts')}
              </UncontrolledTooltip>
            </>
          )}
        </div>

        <div className="col-sm-2">
          <Button
            htmlId="addRowEnergy"
            className="btn btn-primary px-4 py-1 calculator-add-button"
            style={{ float: 'right' }}
            text={t('Add Row')}
            onClick={handleAddRow}
            disabled={isAddButtonDisabled}
          />
        </div>
      </div>

      <div className="table-wrapper-calc-grid">
        <Table responsive className="table--bordered table-wrapper-min">
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Type of Construction</th>
              <th>Function</th>
              <th>Area</th>
              <th>Quantity Used</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.constructionType}</td>
                <td>{row.functionType}</td>
                <td>
                  <NumberFormatterSinSeparador value={row.areaValue} />
                </td>
                <td>
                  <NumberFormatterSinSeparador value={row.quantityValue} />
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td className="subtotal-column-calculator" colSpan={4}>
                  Subtotal: <NumberFormatterSinSeparador value={totalCarbonSum} /> KWh
                </td>
              </tr>
            </tfoot>
          )}
        </Table>
        <br />
        <br />
      </div>
    </>
  );
};

export default EnergyConsumptionElectricity;
